/**
 * VibrantAdmin.tsx - Admin > Integrations > Vibrant.
 *
 * Manages the account-scoped Vibrant ordering setup:
 *   - Test codes (labs_vibrant_ordcode) - the orderable codes: each location's
 *     own VASC.../VACP... codes, plus Vibrant's compendium codes (read-only
 *     global rows with no location, orderable at every location)
 *   - Providers (labs_vibrant_providers) - the ordering providers (Vibrant
 *     Provider ID linked to an internal user) required by ORC-12.1 / OBR-16.
 *   - Presets (labs_vibrant_presets) - named test lists for a location or for
 *     all locations, built here or saved from the order form.
 *
 * Modeled on PatientTagsAdmin: PageHeader + DataTable + Modal + ConfirmDialog.
 *
 * Locked (plan 2026-10-10-001 U7: R8, R18): no Add button and no row actions
 * on any tab, and no empty-list hint inviting an add; nothing says why. A
 * test code or provider row already shows every field its dialog holds, so
 * those rows open nothing. A preset row opens the builder as a viewer: its
 * fields read-only, its tests listed with no test search or remove, and a
 * single Close.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Plus, FlaskConical, UserCog, BookMarked, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable } from '@/components/ui/DataTable';
import type { DataTableColumn, DataTableAction } from '@/components/ui/DataTable';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import {
  listVibrantTests,
  createVibrantTest,
  updateVibrantTest,
  deleteVibrantTest,
  listVibrantProvidersAdmin,
  createVibrantProvider,
  updateVibrantProvider,
  deleteVibrantProvider,
  getVibrantLocations,
  listVibrantPresetsAdmin,
  updateVibrantPreset,
  deleteVibrantPreset,
  saveVibrantPreset,
  getVibrantPresetContent,
  searchVibrantTests,
  type VibrantCatalogTest,
  type VibrantProviderRow,
  type VibrantLocation,
  type VibrantPresetRow,
  type QuestTestCode,
  type QuestOrderNote,
} from '@/services/labs.service';
import { getErrorMessage } from '@/services/apiClient';
import * as adminSvc from '@/services/admin.service';
import { buildUserChoices } from '@/lib/userChoices';
import { parseVibrantPreset } from '@/pages/patients/labs/vibrantPreset';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';

/** Render a location as "Title (routingId)", or the id alone, or a dash. */
function locationLabel(location: string, routingId: string): React.ReactNode {
  if (location && routingId) return <span className="text-sm text-slate-800">{location} <span className="font-mono text-xs text-slate-500">({routingId})</span></span>;
  if (location) return <span className="text-sm text-slate-800">{location}</span>;
  if (routingId) return <span className="font-mono text-sm text-slate-500">{routingId}</span>;
  return <span className="text-slate-400">—</span>;
}

// ════════════════════════════════════════════════════════════════════════════
// Test Codes tab
// ════════════════════════════════════════════════════════════════════════════

interface TestForm {
  testCode: string;
  description: string;
  routingId: string;
  active: boolean;
}

const EMPTY_TEST: TestForm = {
  testCode: '',
  description: '',
  routingId: '',
  active: true,
};

function TestModal({ test, locations, onClose, onSaved }: { test?: VibrantCatalogTest | null; locations: VibrantLocation[]; onClose: () => void; onSaved: () => void }) {
  const isEdit = test != null;
  const [form, setForm] = useState<TestForm>(() =>
    isEdit
      ? {
          testCode: test.testCode,
          description: test.description,
          routingId: test.routingId ?? '',
          active: test.active,
        }
      : EMPTY_TEST,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const locationOptions = locations.map((l) => ({
    value: l.routingId,
    label: l.routingTitle ? `${l.routingTitle} (${l.routingId})` : l.routingId,
  }));

  function set<K extends keyof TestForm>(field: K, value: TestForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.testCode.trim() || !form.description.trim()) return;

    const payload = {
      testCode: form.testCode.trim(),
      description: form.description.trim(),
      routingId: form.routingId.trim(),
      active: form.active,
    };

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateVibrantTest(test.id, payload);
      } else {
        await createVibrantTest(payload);
      }
      onSaved();
      onClose();
    } catch {
      setError('Failed to save test code. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Test Code' : 'Add Test Code'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !form.testCode.trim() || !form.description.trim()} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Test Code'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <FormField label="Order Code" required>
          <Input
            value={form.testCode}
            onChange={(e) => set('testCode', e.target.value)}
            placeholder="e.g. VASC727496"
            required
            autoFocus
          />
        </FormField>

        <FormField label="Description" required>
          <Input
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            placeholder="e.g. Total Baseline (Male)"
            required
          />
        </FormField>

        <FormField label="Location">
          <Select<string>
            options={locationOptions}
            value={form.routingId}
            onChange={(v) => set('routingId', v ?? '')}
            placeholder="Select a location…"
            searchable
            clearable
          />
        </FormField>

        <FormField label="">
          <Checkbox checked={form.active} onChange={(e) => set('active', e.target.checked)} label="Active" />
        </FormField>
      </form>
    </Modal>
  );
}

function TestCodesTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [tests, setTests] = useState<VibrantCatalogTest[]>([]);
  const [locations, setLocations] = useState<VibrantLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState<VibrantCatalogTest | null>(null);
  const [deleteItem, setDeleteItem] = useState<VibrantCatalogTest | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, locs] = await Promise.all([listVibrantTests(), getVibrantLocations()]);
      setTests(rows);
      setLocations(locs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    await deleteVibrantTest(deleteItem.id);
    setDeleteItem(null);
    void load();
  }

  const columns: DataTableColumn<VibrantCatalogTest>[] = [
    {
      key: 'testCode',
      header: 'Order Code',
      sortable: true,
      render: (row) => <span className="font-mono text-sm text-slate-900">{row.testCode}</span>,
    },
    {
      key: 'description',
      header: 'Description',
      sortable: true,
      render: (row) => <span className="text-slate-800">{row.description}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      render: (row) => locationLabel(row.location, row.routingId),
    },
    {
      key: 'active',
      header: 'Status',
      render: (row) => (
        <div className="flex items-center gap-1.5">
          <Badge variant={row.active ? 'success' : 'neutral'}>{row.active ? 'Active' : 'Inactive'}</Badge>
          {row.global && <Badge variant="info">Global</Badge>}
        </div>
      ),
    },
  ];

  // Locked: no row actions (the row already shows every field the dialog holds).
  const rowActions: DataTableAction<VibrantCatalogTest>[] = locked
    ? []
    : [
        { label: 'Edit', onClick: (row) => setEditItem(row), hidden: (row) => row.global },
        { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row), hidden: (row) => row.global },
      ];

  return (
    <div className="p-6">
      <DataTable<VibrantCatalogTest>
        data={tests}
        columns={columns}
        keyField="id"
        loading={loading}
        rowActions={rowActions}
        emptyTitle="No test codes found"
        emptyDescription={locked ? '' : 'Add your first Vibrant order code (e.g. VASC727496) to get started.'}
      />

      {showCreate && !locked && <TestModal locations={locations} onClose={onCreated} onSaved={load} />}
      {editItem && <TestModal test={editItem} locations={locations} onClose={() => setEditItem(null)} onSaved={load} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Test Code"
        message={deleteItem ? `Are you sure you want to delete "${deleteItem.testCode} - ${deleteItem.description}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Providers tab
// ════════════════════════════════════════════════════════════════════════════

interface ProviderForm {
  providerId: string;
  userId: number | null;
  /**
   * Every location Vibrant has this number set up at. Vibrant sets a number up
   * per (practice, provider) pair: one number can belong to several locations,
   * and a provider can have a different number at each.
   */
  routingIds: string[];
}

function ProviderModal({
  provider,
  users,
  locations,
  onClose,
  onSaved,
}: {
  provider?: VibrantProviderRow | null;
  users: adminSvc.AdminUser[];
  locations: VibrantLocation[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = provider != null;
  const [form, setForm] = useState<ProviderForm>(() =>
    isEdit
      ? {
          providerId: provider.providerId,
          userId: provider.userId || null,
          routingIds: provider.locations.map((l) => l.routingId),
        }
      : { providerId: '', userId: null, routingIds: [] },
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // /admin/users includes deactivated staff; drop them, but keep whoever this
  // provider is already mapped to so an existing mapping stays visible.
  const userOptions = buildUserChoices(users, form.userId)
    .map((c) => ({ value: c.userId, label: c.label }));

  const locationOptions = locations.map((l) => ({
    value: l.routingId,
    label: l.routingTitle ? `${l.routingTitle} (${l.routingId})` : l.routingId,
  }));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.providerId.trim() || !form.userId || form.routingIds.length === 0) return;

    const payload = { providerId: form.providerId.trim(), userId: form.userId, routingIds: form.routingIds };
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateVibrantProvider(provider.id, payload);
      } else {
        await createVibrantProvider(payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, "Couldn't save this provider. Please try again."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? 'Edit Provider' : 'Add Provider'}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !form.providerId.trim() || !form.userId || form.routingIds.length === 0} loading={saving}>
            {isEdit ? 'Save Changes' : 'Add Provider'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <FormField label="User" required>
          <Select<number>
            options={userOptions}
            value={form.userId}
            onChange={(v) => setForm((f) => ({ ...f, userId: v }))}
            placeholder="Select a user…"
            searchable
          />
        </FormField>

        <FormField label="Vibrant Provider ID" required>
          <Input
            value={form.providerId}
            onChange={(e) => setForm((f) => ({ ...f, providerId: e.target.value }))}
            placeholder="Vibrant Provider ID or NPI"
            required
          />
        </FormField>

        <FormField
          label="Locations"
          required
          helpText="Choose every location where Vibrant has this provider number set up."
        >
          <SearchableMultiSelect
            options={locationOptions}
            selectedValues={form.routingIds}
            onSelectionChange={(values) => setForm((f) => ({ ...f, routingIds: values }))}
            placeholder="Select locations…"
            searchPlaceholder="Search locations…"
            emptyMessage="No locations found."
          />
        </FormField>
      </form>
    </Modal>
  );
}

function ProvidersTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [providers, setProviders] = useState<VibrantProviderRow[]>([]);
  const [users, setUsers] = useState<adminSvc.AdminUser[]>([]);
  const [locations, setLocations] = useState<VibrantLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState<VibrantProviderRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<VibrantProviderRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [provs, usrs, locs] = await Promise.all([listVibrantProvidersAdmin(), adminSvc.listUsers(), getVibrantLocations()]);
      setProviders(provs);
      setUsers(usrs);
      setLocations(locs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    await deleteVibrantProvider(deleteItem.id);
    setDeleteItem(null);
    void load();
  }

  const columns: DataTableColumn<VibrantProviderRow>[] = [
    {
      key: 'providerName',
      header: 'Provider',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-900">{row.providerName || `User #${row.userId}`}</span>,
    },
    {
      key: 'providerId',
      header: 'Vibrant Provider ID',
      render: (row) => <span className="font-mono text-sm text-slate-700">{row.providerId}</span>,
    },
    {
      key: 'locations',
      header: 'Locations',
      // An entry with no location is hidden from every order form.
      render: (row) => (
        <div className="flex flex-wrap items-center gap-1">
          {row.locations.map((l) => (
            <Badge key={l.routingId} variant="neutral">{l.routingTitle || l.routingId}</Badge>
          ))}
          {row.locations.length === 0 && <Badge variant="warning">Choose a location</Badge>}
        </div>
      ),
    },
    {
      key: 'active',
      header: 'Status',
      render: (row) => <Badge variant={row.active ? 'success' : 'neutral'}>{row.active ? 'Active' : 'Inactive'}</Badge>,
    },
  ];

  // Locked: no row actions (the row already shows every field the dialog holds).
  const rowActions: DataTableAction<VibrantProviderRow>[] = locked
    ? []
    : [
        { label: 'Edit', onClick: (row) => setEditItem(row) },
        { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row), hidden: (row) => !row.active },
      ];

  return (
    <div className="p-6">
      <DataTable<VibrantProviderRow>
        data={providers}
        columns={columns}
        keyField="id"
        loading={loading}
        rowActions={rowActions}
        emptyTitle="No providers found"
        emptyDescription={locked ? '' : 'Add an ordering provider (Vibrant Provider ID linked to a user) to enable ordering.'}
      />

      {showCreate && !locked && <ProviderModal users={users} locations={locations} onClose={onCreated} onSaved={load} />}
      {editItem && <ProviderModal provider={editItem} users={users} locations={locations} onClose={() => setEditItem(null)} onSaved={load} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Remove Provider"
        message={deleteItem ? `Are you sure you want to remove "${deleteItem.providerName || deleteItem.providerId}"?` : ''}
        confirmLabel="Remove"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Presets tab
// ════════════════════════════════════════════════════════════════════════════

/** Location value meaning "every location" (stored as an empty routing id). */
const ALL_LOCATIONS = '*';

interface BuilderTest {
  testCode: string;
  description: string;
  obrNotes: QuestOrderNote[];
  /**
   * The location the code is tied to ('' = orderable everywhere). Unknown
   * (undefined) for tests loaded from a saved preset; the server re-checks
   * every code on save either way.
   */
  routingId?: string;
}

/**
 * Build or edit a preset: name it, pick the location it is for (or all
 * locations), search the codes orderable there and add them. The admin
 * counterpart of Save Preset on the order form, the way legacy Quest opened
 * its order form in admin mode to build presets.
 */
function PresetBuilderModal({
  preset,
  locations,
  onClose,
  onSaved,
}: {
  preset?: VibrantPresetRow | null;
  locations: VibrantLocation[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = preset != null;
  const presetId = preset?.id;
  // Locked: a viewer — fields read-only, no test search or remove, nothing saves.
  const locked = useAdminPageLevel() === 'locked';
  const [title, setTitle] = useState(preset?.title ?? '');
  const [shared, setShared] = useState(preset?.shared ?? true);
  const [scope, setScope] = useState<string>(preset?.routingId ? preset.routingId : ALL_LOCATIONS);
  const [tests, setTests] = useState<BuilderTest[]>([]);
  const [loadingTests, setLoadingTests] = useState(isEdit);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<QuestTestCode[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const locationOptions = [
    { value: ALL_LOCATIONS, label: 'All locations' },
    ...locations.map((l) => ({
      value: l.routingId,
      label: l.routingTitle ? `${l.routingTitle} (${l.routingId})` : l.routingId,
    })),
  ];

  // Editing: load the tests the preset holds today.
  useEffect(() => {
    if (presetId == null) return;
    let cancelled = false;
    getVibrantPresetContent('preset', presetId)
      .then((content) => {
        if (cancelled) return;
        const parsed = parseVibrantPreset(content.fullOrderContent, 'preset', '');
        setTests(parsed.tests.map((t) => ({ testCode: t.testCode, description: t.description, obrNotes: t.obrNotes, routingId: t.routingId })));
        // Tests that can no longer be ordered are left out by the server;
        // saving writes the preset without them.
        const gone = content.unavailableTests ?? [];
        if (gone.length > 0) {
          const names = gone.map((g) => (g.description ? `${g.description} (${g.testCode})` : g.testCode)).join(', ');
          const count = gone.length === 1 ? '1 test' : `${gone.length} tests`;
          setNotice(
            locked
              ? `${count} can no longer be ordered: ${names}.`
              : `Removed ${count} that can no longer be ordered: ${names}. Save to update the preset.`,
          );
        }
      })
      .catch(() => {
        if (!cancelled) setError("This preset's tests could not be loaded. Close this window and try again.");
      })
      .finally(() => {
        if (!cancelled) setLoadingTests(false);
      });
    return () => {
      cancelled = true;
    };
  }, [presetId, locked]);

  // Search the codes orderable for the chosen location (debounced). An empty
  // search lists the first codes so there is something to pick from on open.
  useEffect(() => {
    if (locked) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      const request =
        scope === ALL_LOCATIONS
          ? searchVibrantTests(search.trim(), undefined, { globalOnly: true })
          : searchVibrantTests(search.trim(), scope);
      request
        .then((rows) => {
          if (!cancelled) setResults(rows);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, scope, locked]);

  function changeScope(next: string) {
    const nextRoutingId = next === ALL_LOCATIONS ? '' : next;
    // A location's own codes cannot come along to a different location.
    const kept = tests.filter((t) => t.routingId === undefined || t.routingId === '' || t.routingId === nextRoutingId);
    const dropped = tests.length - kept.length;
    setScope(next);
    setTests(kept);
    setNotice(
      dropped > 0
        ? `Removed ${dropped} ${dropped === 1 ? 'test' : 'tests'} that cannot be ordered at the new location.`
        : null,
    );
  }

  function addTest(row: QuestTestCode) {
    setTests((prev) =>
      prev.some((t) => t.testCode === row.testCode)
        ? prev
        : [...prev, { testCode: row.testCode, description: row.description, obrNotes: [], routingId: row.routingId ?? '' }],
    );
  }

  function removeTest(testCode: string) {
    setTests((prev) => prev.filter((t) => t.testCode !== testCode));
  }

  const canSave = !locked && title.trim() !== '' && tests.length > 0 && !saving && !loadingTests;

  async function handleSubmit(e?: React.FormEvent) {
    e?.preventDefault();
    if (!canSave) return;

    const routingId = scope === ALL_LOCATIONS ? '' : scope;
    const payloadTests = tests.map(({ testCode, description, obrNotes }) => ({ testCode, description, obrNotes }));

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateVibrantPreset(preset.id, { title: title.trim(), shared: shared ? '1' : '0', routingId, tests: payloadTests });
      } else {
        await saveVibrantPreset({
          presetTitle: title.trim(),
          presetShared: shared ? '1' : '0',
          routingId,
          tests: payloadTests,
          createOnly: true,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, 'The preset could not be saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  }

  const selectedCodes = new Set(tests.map((t) => t.testCode));

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Preset' : isEdit ? 'Edit Preset' : 'Add Preset'}
      size="lg"
      footer={
        locked ? (
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={!canSave} loading={saving}>
              {isEdit ? 'Save Changes' : 'Add Preset'}
            </Button>
          </>
        )
      }
    >
      <AdminLockedFieldset>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
        )}

        <FormField label="Title" required>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={locked ? undefined : 'e.g. Annual Wellness'} required autoFocus={!locked} />
        </FormField>

        <FormField label="Location">
          <Select<string>
            options={locationOptions}
            value={scope}
            onChange={(v) => changeScope(v ?? ALL_LOCATIONS)}
            searchable
          />
        </FormField>

        <FormField label="">
          <Checkbox checked={shared} onChange={(e) => setShared(e.target.checked)} label="Shared with all users on this account" />
        </FormField>

        {!locked && (
        <FormField label="Add Tests">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by test name or code..."
            aria-label="Search tests"
          />
          <div className="mt-2 max-h-56 overflow-y-auto rounded border border-slate-200" data-testid="preset-test-results">
            {results.length === 0 ? (
              <p className="px-3 py-2 text-sm text-slate-500">{searching ? 'Searching...' : 'No matching tests.'}</p>
            ) : (
              results.map((row) => {
                const added = selectedCodes.has(row.testCode);
                return (
                  <button
                    key={`${row.testCode}|${row.routingId ?? ''}`}
                    type="button"
                    onClick={() => addTest(row)}
                    disabled={added}
                    className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50 disabled:cursor-default disabled:bg-slate-50 disabled:text-slate-400"
                  >
                    <span className="font-mono text-xs text-slate-500">{row.testCode}</span>
                    <span className="flex-1 text-slate-800">{row.description}</span>
                    {added ? <span className="text-xs">Added</span> : <Plus className="h-4 w-4 text-slate-400" />}
                  </button>
                );
              })
            )}
          </div>
        </FormField>
        )}

        <FormField label={`Tests in this preset (${tests.length})`}>
          {notice && <p className="mb-2 text-sm text-amber-700">{notice}</p>}
          {loadingTests ? (
            <p className="text-sm text-slate-500">Loading...</p>
          ) : tests.length === 0 ? (
            <p className="text-sm text-slate-500">{locked ? 'No tests.' : 'No tests added yet.'}</p>
          ) : (
            <ul className="divide-y divide-slate-100 rounded border border-slate-200" data-testid="preset-selected-tests">
              {tests.map((t) => (
                <li key={t.testCode} className="flex items-center gap-2 px-3 py-2 text-sm">
                  <span className="font-mono text-xs text-slate-500">{t.testCode}</span>
                  <span className="flex-1 text-slate-800">{t.description}</span>
                  {!locked && (
                    <button
                      type="button"
                      onClick={() => removeTest(t.testCode)}
                      className="text-slate-400 hover:text-red-600"
                      aria-label={`Remove ${t.description || t.testCode}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </FormField>
      </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

function PresetsTab({ showCreate, onCreated }: { showCreate: boolean; onCreated: () => void }) {
  const locked = useAdminPageLevel() === 'locked';
  const [presets, setPresets] = useState<VibrantPresetRow[]>([]);
  const [locations, setLocations] = useState<VibrantLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [editItem, setEditItem] = useState<VibrantPresetRow | null>(null);
  const [deleteItem, setDeleteItem] = useState<VibrantPresetRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rows, locs] = await Promise.all([listVibrantPresetsAdmin(), getVibrantLocations()]);
      setPresets(rows);
      setLocations(locs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleDelete() {
    if (!deleteItem) return;
    await deleteVibrantPreset(deleteItem.id);
    setDeleteItem(null);
    void load();
  }

  const columns: DataTableColumn<VibrantPresetRow>[] = [
    {
      key: 'title',
      header: 'Title',
      sortable: true,
      render: (row) => <span className="font-medium text-slate-900">{row.title}</span>,
    },
    {
      key: 'location',
      header: 'Location',
      sortable: true,
      render: (row) => locationLabel(row.location, row.routingId),
    },
    {
      key: 'testCount',
      header: 'Tests',
      render: (row) => <span className="text-sm text-slate-600">{row.testCount}</span>,
    },
    {
      key: 'shared',
      header: 'Shared',
      render: (row) => <Badge variant={row.shared ? 'info' : 'neutral'}>{row.shared ? 'Shared' : 'Private'}</Badge>,
    },
  ];

  // Locked: no row actions; a row click opens the preset as a viewer.
  const rowActions: DataTableAction<VibrantPresetRow>[] = locked
    ? []
    : [
        { label: 'Edit', onClick: (row) => setEditItem(row) },
        { label: 'Delete', variant: 'danger', onClick: (row) => setDeleteItem(row) },
      ];

  return (
    <div className="p-6">
      <DataTable<VibrantPresetRow>
        data={presets}
        columns={columns}
        keyField="id"
        loading={loading}
        rowActions={rowActions}
        onRowClick={locked ? (row) => setEditItem(row) : undefined}
        emptyTitle="No presets yet"
        emptyDescription=""
      />

      {showCreate && !locked && <PresetBuilderModal locations={locations} onClose={onCreated} onSaved={load} />}
      {editItem && <PresetBuilderModal preset={editItem} locations={locations} onClose={() => setEditItem(null)} onSaved={load} />}

      <ConfirmDialog
        isOpen={deleteItem !== null}
        title="Delete Preset"
        message={deleteItem ? `Are you sure you want to delete the preset "${deleteItem.title}"?` : ''}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onClose={() => setDeleteItem(null)}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Page
// ════════════════════════════════════════════════════════════════════════════

export function VibrantAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [activeTab, setActiveTab] = useState('tests');
  const [showCreate, setShowCreate] = useState(false);

  const addLabel = activeTab === 'providers' ? 'Add Provider' : activeTab === 'presets' ? 'Add Preset' : 'Add Test Code';

  return (
    <div>
      <div className="px-6 pt-6">
        <PageHeader
          title="Vibrant"
          actions={
            locked ? undefined : (
              <Button onClick={() => setShowCreate(true)}>
                <Plus className="h-4 w-4 mr-1" />
                {addLabel}
              </Button>
            )
          }
        />
      </div>

      <Tabs
        tabs={[
          { id: 'tests', label: 'Test Codes', icon: <FlaskConical className="h-4 w-4" />, content: <TestCodesTab showCreate={showCreate && activeTab === 'tests'} onCreated={() => setShowCreate(false)} /> },
          { id: 'providers', label: 'Providers', icon: <UserCog className="h-4 w-4" />, content: <ProvidersTab showCreate={showCreate && activeTab === 'providers'} onCreated={() => setShowCreate(false)} /> },
          { id: 'presets', label: 'Presets', icon: <BookMarked className="h-4 w-4" />, content: <PresetsTab showCreate={showCreate && activeTab === 'presets'} onCreated={() => setShowCreate(false)} /> },
        ]}
        onChange={(id) => { setActiveTab(id); setShowCreate(false); }}
      />
    </div>
  );
}
